import { displayPlainText } from '@/utils/stripHtmlForTooltip';

/**
 * Central EBV UI capability matrix for public search.
 *
 * ## Inputs
 *
 * | Source | Setting / field | What it carries |
 * |--------|---------|-----------------|
 * | Setting 26 | `EBV_DISPLAY_OPTIONS` | `ebvProvider`, `ebvStrainMode`, `ebvBoxOptionsRaw`, i4 flags, `ebvLabel` |
 * | Setting 15 | EBV averages JSON | Numeric / dual-line `ebv_N_avg` (+ `_b`/`_r`), captions |
 * | Setting 16 | `EBV_PERCENTAGES` | Standard P% band rows |
 * | Setting 29 | `EBV_PERCENTAGES_BR` | CANG B% / R% band rows |
 *
 * Setting 26 may also emit `i4ShowAverage` (i4 `average` token). Public search
 * **ignores** it: breed-average UI is shown when setting 15 has usable values.
 * i4 treated `average` as opt-in; we prefer live data so societies with avgs
 * but a missing token still show the row.
 *
 * ## Provider base matrix (`ebvProvider`)
 *
 * | Flag | None | Breedplan | Custom |
 * |------|------|-----------|--------|
 * | showPotentialChangeGraph | off | on | off |
 * | showBreedplanHelp | off | on | off |
 * | showPercentileSearch | off | on | on |
 * | showTraitsObserved | on | on | on |
 * | showPercentileGraph | off | on | on |
 * | showAccuracy | off | on | on |
 * | showBreedAverages | off | on | on |
 * | showRowLabels | off | on | on |
 *
 * ## Live-data and token overrides (`mergeEbvDisplayOptions`)
 *
 * | Effective flag | Requires | Hidden when |
 * |------|--------------------|-----------|
 * | showBreedAverages | provider allows + setting 15 has usable avgs | no numeric/HTML avgs (dairy null-avg) |
 * | showPercentileSearch | provider allows + bands (16, or 29 for BlackRedAngus) | BlackRedAngus without setting 29 |
 * | showPercentileGraph | same as search + not `i4NoGraph` | same |
 * | showAccuracy | provider allows + not `i4NoAcc` | values-only (no avgs and no bands) |
 * | showRowLabels | `i4ShowHeading` **or** Acc/percentile UI shown | values-only without heading |
 * | showTopPercentRow | BlackRedAngus + `i4ShowTopPercent` (or raw `toppercent`) + setting 29 | otherwise |
 * | useColourSlotGrouping | BlackRedAngus strain | otherwise |
 * | showTraitsObserved | provider allows | `i4NoObservedTraits` |
 * | showPotentialChangeGraph / showBreedplanHelp | provider base only | — |
 *
 * **Values-only profile** (typical dairy ABVs): no setting-15 values and no
 * percentile bands → hide Acc, %, breed-avg block, and left-row labels
 * (unless `i4ShowHeading`).
 */

type EbvProvider = 'None' | 'Breedplan' | 'Custom';

type EbvStrainMode = 'None' | 'BlackRedAngus';

type EbvDisplayCapabilities = {
  showPotentialChangeGraph: boolean;
  showBreedplanHelp: boolean;
  showPercentileSearch: boolean;
  showTraitsObserved: boolean;
  showPercentileGraph: boolean;
  /** Acc column / accuracy toggle (hidden when i4 noacc or values-only). */
  showAccuracy: boolean;
  /** Breed-average row / table (needs usable avgs in setting 15). */
  showBreedAverages: boolean;
  /**
   * Left-row labels (EBV / Acc / Percentile). i4 `heading` opt-in, or implied when
   * Acc / percentile rows are shown. Values-only (dairy) omits this gutter.
   */
  showRowLabels: boolean;
};

const NONE_CAPABILITIES: EbvDisplayCapabilities = {
  showPotentialChangeGraph: false,
  showBreedplanHelp: false,
  showPercentileSearch: false,
  showTraitsObserved: true,
  showPercentileGraph: false,
  showAccuracy: false,
  showBreedAverages: false,
  showRowLabels: false,
};

const BREEDPLAN_CAPABILITIES: EbvDisplayCapabilities = {
  showPotentialChangeGraph: true,
  showBreedplanHelp: true,
  showPercentileSearch: true,
  showTraitsObserved: true,
  showPercentileGraph: true,
  showAccuracy: true,
  showBreedAverages: true,
  showRowLabels: true,
};

const CUSTOM_CAPABILITIES: EbvDisplayCapabilities = {
  showPotentialChangeGraph: false,
  showBreedplanHelp: false,
  showPercentileSearch: true,
  showTraitsObserved: true,
  showPercentileGraph: true,
  showAccuracy: true,
  showBreedAverages: true,
  showRowLabels: true,
};

/** CANG (Custom + BlackRedAngus): EPD values without BREEDPLAN-style percentile UI unless B/R bands exist. */
export function isBlackRedAngusStrain(
  strainMode: EbvStrainMode | string | null | undefined,
): boolean {
  return strainMode === 'BlackRedAngus';
}

/** Base capability matrix by provider; BlackRedAngus strain overrides apply in mergeEbvDisplayOptions. */
function getEbvProviderCapabilities(
  provider: EbvProvider | string | null | undefined,
): EbvDisplayCapabilities {
  switch (provider) {
    case 'Breedplan':
      return BREEDPLAN_CAPABILITIES;
    case 'Custom':
      return CUSTOM_CAPABILITIES;
    case 'None':
      return NONE_CAPABILITIES;
    default:
      return CUSTOM_CAPABILITIES;
  }
}

export type EbvDisplayOptionsJson = {
  ebvProvider?: EbvProvider | string;
  ebvStrainMode?: EbvStrainMode | string;
  ebvBoxOptionsRaw?: string | null;
  i4HasVersion2?: boolean;
  i4NoObservedTraits?: boolean;
  i4NoGraph?: boolean;
  /** i4 ebvBoxOptions `noacc` — hide accuracy UI. */
  i4NoAcc?: boolean;
  /** i4 ebvBoxOptions `heading` — opt-in left-row labels (EBV / Acc / Percentile). */
  i4ShowHeading?: boolean;
  /** i4 ebvBoxOptions `toppercent` — CANG TopPercent row from setting 29 bands. */
  i4ShowTopPercent?: boolean;
  ebvLabel?: string;
};

export type MergeEbvDisplayOptionsContext = {
  /** Setting 29 populated with B/R percentile bands. */
  hasBrPercentileBands?: boolean;
  /** Setting 16 (P%) has percentile band rows. */
  hasPercentileBands?: boolean;
  /** Setting 15 has any usable breed-average value (see {@link hasAnyNumericBreedAverage}). */
  hasAnyNumericBreedAverage?: boolean;
};

export type EffectiveEbvDisplayOptions = EbvDisplayCapabilities & {
  ebvProvider: EbvProvider;
  ebvStrainMode: EbvStrainMode;
  ebvLabel: string;
  /** Convenience: `ebvStrainMode === 'BlackRedAngus'`. Prefer this over re-checking strain. */
  isBlackRedAngus: boolean;
  /**
   * Group/sort/tint EBV columns by COL `chartColour` slot (CANG).
   * Same as {@link isBlackRedAngus} today; exposed so call sites need not re-derive strain.
   */
  useColourSlotGrouping: boolean;
  /** CANG TopPercent: percentile row caption stays "Percentile"; values from B/R band lookup (i4 CalculateTopPercent). */
  showTopPercentRow: boolean;
  raw: EbvDisplayOptionsJson | null;
};

/** Plural tab/column label from society setting 26 `ebvLabel` (e.g. EBV → EBVs, EPD → EPDs). */
export function formatEbvPluralLabel(ebvLabel: string): string {
  const trimmed = ebvLabel.trim();
  if (!trimmed) return 'EBVs';
  if (/s$/i.test(trimmed)) return trimmed;
  return `${trimmed}s`;
}

/** Accuracy column/toggle label (e.g. EBV → EBV Accuracy, EPD → EPD Accuracy). */
export function formatEbvAccuracyLabel(ebvLabel: string): string {
  const trimmed = ebvLabel.trim() || 'EBV';
  return `${trimmed} Accuracy`;
}

/** Min value filter label (e.g. Min EBV, Min EPD, Min ABV). */
export function formatMinValueLabel(valueLabel: string): string {
  return `Min ${valueLabel.trim() || 'EBV'}`;
}

/** Max value filter label (e.g. Max EBV, Max EPD, Max ABV). */
export function formatMaxValueLabel(valueLabel: string): string {
  return `Max ${valueLabel.trim() || 'EBV'}`;
}

/** Basic Search / Advanced Search tab title (e.g. EBVs and Indexes, EPDs and Indexes). */
export function formatEbvAndIndexesTabLabel(ebvLabel: string): string {
  return `${formatEbvPluralLabel(ebvLabel)} and Indexes`;
}

/**
 * Breed-average row / footer caption.
 * Breedplan (i4 @616): "50th Percentile is the {description}".
 * Custom / no percentiles: short "Breed Avg. EPDs" without cohort suffix.
 */
export function formatBreedAverageCaption(
  ebvLabel: string,
  options: {
    showPercentileContext: boolean;
    description?: string | null;
  },
): string {
  const plural = formatEbvPluralLabel(ebvLabel);
  const shortCaption = `Breed Avg. ${plural}`;
  if (!options.showPercentileContext) {
    return shortCaption;
  }
  const trimmed = options.description?.trim();
  if (trimmed) {
    return `50th Percentile is the ${trimmed}`;
  }
  return `50th Percentile is the ${shortCaption}`;
}

/**
 * True when setting 15 has any usable breed-average (or SE) value.
 * Dairy-style A/M label rows with all-null avgs return false — UI sits on the
 * society model floor without an empty average block below.
 *
 * Accepts plain numbers and ILR2 dual-line / font-wrapped HTML that still
 * contains a digit after tags are removed. Lone placeholders like `-` do not count.
 */
export function hasAnyNumericBreedAverage(
  data: Record<string, unknown> | null | undefined,
): boolean {
  if (!data) return false;

  for (const [key, value] of Object.entries(data)) {
    if (!/^ebv_\d+_avg(_[br])?$/i.test(key) && !/^ebv_\d+_se$/i.test(key)) {
      continue;
    }
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'number' && !Number.isNaN(value)) return true;
    const text = String(value).trim();
    if (!text) continue;
    const n = Number(text.replace(/,/g, ''));
    if (!Number.isNaN(n)) return true;
    // ILR2 HTML averages (e.g. `+3.3<br><font color=#800040>+2.2</font>`).
    if (/<\s*(br|font)\b/i.test(text) && /\d/.test(displayPlainText(text))) {
      return true;
    }
  }

  return false;
}

export function mergeEbvDisplayOptions(
  raw: EbvDisplayOptionsJson | null,
  context: MergeEbvDisplayOptionsContext = {},
): EffectiveEbvDisplayOptions {
  const provider = normalizeEbvProvider(raw?.ebvProvider);
  const strainMode = normalizeEbvStrainMode(raw?.ebvStrainMode);
  const base = getEbvProviderCapabilities(provider);
  const blackRedAngus = isBlackRedAngusStrain(strainMode);
  const hasBrBands = Boolean(context.hasBrPercentileBands);
  const hasStandardBands = Boolean(context.hasPercentileBands);
  const hasNumericAverages = Boolean(context.hasAnyNumericBreedAverage);
  const hasPercentileBandData =
    hasStandardBands || (blackRedAngus && hasBrBands);
  // BlackRedAngus without B/R bands: no BREEDPLAN-style P% UI.
  const suppressBlackRedPercentiles = blackRedAngus && !hasBrBands;
  // Societies with no avg values and no percentile bands (dairy ABVs): values only.
  const valuesOnlyProfile = !hasNumericAverages && !hasPercentileBandData;
  // Prefer setting-26 token; fall back to raw string until setting 26 is reseeded.
  const i4ShowTopPercent =
    Boolean(raw?.i4ShowTopPercent) ||
    (raw?.ebvBoxOptionsRaw?.toLowerCase() ?? '').includes('toppercent');
  const showTopPercentRow = blackRedAngus && i4ShowTopPercent && hasBrBands;

  // Setting 15 data wins over the unused i4 `average` token (see file header).
  const showBreedAverages = base.showBreedAverages && hasNumericAverages;

  const showPercentileSearch =
    base.showPercentileSearch &&
    hasPercentileBandData &&
    !suppressBlackRedPercentiles;

  const showPercentileGraph =
    base.showPercentileGraph &&
    hasPercentileBandData &&
    !Boolean(raw?.i4NoGraph) &&
    !suppressBlackRedPercentiles;

  const showAccuracy =
    base.showAccuracy && !Boolean(raw?.i4NoAcc) && !valuesOnlyProfile;

  // i4 `heading` is opt-in. Acc / percentile rows always need a left label;
  // values-only societies (dairy) omit the gutter so trait columns start at LPI/BPI.
  const showRowLabels =
    Boolean(raw?.i4ShowHeading) || showAccuracy || showPercentileSearch;

  return {
    ebvProvider: provider,
    ebvStrainMode: strainMode,
    ebvLabel: raw?.ebvLabel?.trim() || 'EBV',
    isBlackRedAngus: blackRedAngus,
    useColourSlotGrouping: blackRedAngus,
    raw,
    showTopPercentRow,
    showPotentialChangeGraph: base.showPotentialChangeGraph,
    showBreedplanHelp: base.showBreedplanHelp,
    showPercentileSearch,
    showTraitsObserved:
      base.showTraitsObserved && !Boolean(raw?.i4NoObservedTraits),
    showPercentileGraph,
    showAccuracy,
    showBreedAverages,
    showRowLabels,
  };
}

function normalizeEbvProvider(value: unknown): EbvProvider {
  if (value === 'Breedplan' || value === 'Custom' || value === 'None') {
    return value;
  }
  return 'Custom';
}

function normalizeEbvStrainMode(value: unknown): EbvStrainMode {
  if (value === 'BlackRedAngus' || value === 'None') {
    return value;
  }
  return 'None';
}
